import type { ItemType, ItemStatus, MediaStatus, SourceStatus, ProgramType, BugType, LogoProfile, ScheduleItem } from '../types/index.js'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { broadcastDayKey, todayBroadcastDayKey } from './broadcast-day.js'
import { getServerNow } from '../hooks/useServerClock.js'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Valeur du sélecteur « Logo Antenne » qui coupe le logo pour de bon. Elle ne
 * désigne aucun profil : un fichier qui porte déjà un logo incrusté ne doit pas
 * en recevoir un second. Même sentinelle côté serveur, où elle vaut aussi comme
 * surcharge de grille (`overrides.logoProfileId`).
 */
export const NO_LOGO_VALUE = 'NONE'

export function defaultLogoProfileId(profiles: readonly LogoProfile[]): string {
  const activeProfiles = profiles.filter(profile => profile.active)
  return (activeProfiles.find(profile => profile.key === 'normal') ?? activeProfiles[0])?.id ?? ''
}

// ─── Durée ────────────────────────────────────────────────────────────────────

/**
 * Convertit des secondes en chaîne lisible HH:MM:SS ou MM:SS.
 * ex : 3661 → "1:01:01"
 */
export function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Version courte pour l'affichage dans les blocs agenda.
 * ex : 3661 → "1h01"  |  300 → "5min"
 */
export function formatDurationShort(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0 && m > 0) return `${h}h${String(m).padStart(2, '0')}`
  if (h > 0)          return `${h}h`
  if (m > 0)          return `${m}min`
  return `${s}s`
}

/**
 * Dérive en secondes → chaîne signée.
 * ex : -47 → "−47s"  |  120 → "+2:00"
 */
export function formatDrift(sec: number): string {
  const sign = sec >= 0 ? '+' : '−'
  return `${sign}${formatDuration(Math.abs(sec))}`
}

// ─── Heure ────────────────────────────────────────────────────────────────────

/**
 * Formate un ISO string en HH:MM:SS.
 * ex : "2025-04-02T13:05:07Z" → "13:05:07"
 */
export function formatTime(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

/**
 * Formate un ISO string en date courte localisée.
 * ex : "Mer 2 avr."
 */
export function formatDateShort(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}

/**
 * Retourne true si la date correspond à la journée d'antenne en cours.
 *
 * « Aujourd'hui » se juge sur l'horloge du serveur et sur le fuseau de la
 * chaîne : c'est la journée que l'antenne est en train de diffuser. Comparé au
 * calendrier du poste, le bouton « Aujourd'hui » s'allumait sur la veille pour
 * un opérateur situé à l'ouest, et basculait avant l'antenne pour un poste en
 * avance.
 */
export function isToday(iso: string): boolean {
  const at = new Date(iso)
  if (isNaN(at.getTime())) return false
  return broadcastDayKey(at) === todayBroadcastDayKey(getServerNow())
}

/** Retourne le lundi local (YYYY-MM-DD) de la semaine contenant une date. */
export function weekMondayISO(dateISO: string): string {
  const [year, month, day] = dateISO.slice(0, 10).split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const weekday = date.getDay()
  date.setDate(date.getDate() + (weekday === 0 ? -6 : 1 - weekday))
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/** Numéro de semaine ISO-8601 de la semaine contenant une date. */
export function isoWeekNumber(dateISO: string): number {
  const [year, month, day] = weekMondayISO(dateISO).split('-').map(Number)
  const thursday = new Date(Date.UTC(year, month - 1, day + 3))
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4))
  const firstDay = firstThursday.getUTCDay() || 7
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 4 - firstDay)
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / 604_800_000)
}

/** Nom éditorial proposé lors de la création d'une grille hebdomadaire. */
export function formatWeekGridName(dateISO: string): string {
  const [year, month, day] = weekMondayISO(dateISO).split('-').map(Number)
  const monday = new Date(year, month - 1, day)
  const sunday = new Date(year, month - 1, day + 6)
  const mondayMonth = monday.toLocaleDateString('fr-FR', { month: 'long' })
  const sundayMonth = sunday.toLocaleDateString('fr-FR', { month: 'long' })
  const range = monday.getMonth() === sunday.getMonth()
    ? `${monday.getDate()} au ${sunday.getDate()} ${sundayMonth}`
    : `${monday.getDate()} ${mondayMonth} au ${sunday.getDate()} ${sundayMonth}`
  return `Semaine ${isoWeekNumber(dateISO)} — ${range}`
}

export function emptyWeekGridLabel(dateISO: string): string {
  return `Grille vide · ${formatWeekGridName(dateISO)}`
}

/**
 * Convertit une heure fractionnaire (ex: 13.5) en pixel Y sur la timeline.
 * PX_PER_HOUR = hauteur d'une heure dans la grille agenda.
 */
export function hourToY(hour: number, pxPerHour: number): number {
  return Math.round(hour * pxPerHour)
}

/**
 * Génère un ID unique court (fallback pour crypto.randomUUID en contexte non-sécurisé).
 */
export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).substring(2, 11) + Date.now().toString(36)
}

/**
 * Convertit un ISO string en position Y sur la timeline du jour courant.
 */
export function isoToY(iso: string, startHour: number, pxPerHour: number): number {
  const d = new Date(iso)
  const totalSeconds = (d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds()) - startHour * 3600
  return Math.round((totalSeconds / 3600) * pxPerHour)
}

/**
 * Convertit une hauteur en pixels en durée en secondes.
 */
export function pxToDuration(px: number, pxPerHour: number): number {
  return Math.round((px / pxPerHour) * 3600)
}

// ─── Couleurs & labels par statut ─────────────────────────────────────────────

/**
 * Couleur d'un element de grille par statut. Le statut prime sur le type :
 * a l'antenne vert, en erreur rouge, passe ou saute gris. Les elements en
 * attente (SCHEDULED, LOADING) prennent la couleur de leur type — voir
 * ITEM_TYPE_COLORS.
 */
export const ITEM_STATUS_COLORS: Record<ItemStatus, {
  bg: string; border: string; text: string; badge: string
}> = {
  SCHEDULED: { bg: 'var(--color-item-program-bg)', border: 'var(--color-item-program-border)', text: 'var(--color-item-program-text)', badge: 'var(--color-item-program-badge)' },
  LOADING:   { bg: 'var(--color-item-live-bg)', border: 'var(--color-item-live-border)', text: 'var(--color-item-live-text)', badge: 'var(--color-item-live-badge)' },
  ON_AIR:    { bg: 'var(--color-item-onair-bg)', border: 'var(--color-item-onair-border)', text: 'var(--color-item-onair-text)', badge: 'var(--color-item-onair-badge)' },
  DONE:      { bg: 'var(--color-item-neutral-bg)', border: 'var(--color-item-neutral-border)', text: 'var(--color-item-neutral-text)', badge: 'var(--color-item-neutral-badge)' },
  GAP:       { bg: 'var(--color-item-interlude-bg)', border: 'var(--color-item-interlude-border)', text: 'var(--color-item-interlude-text)', badge: 'var(--color-item-interlude-badge)' },
  ERROR:     { bg: 'var(--color-background-danger)', border: 'var(--color-border-danger)', text: 'var(--color-text-danger)', badge: 'var(--color-item-error-badge)' },
  SKIPPED:   { bg: 'var(--color-item-neutral-bg)', border: 'var(--color-item-neutral-border)', text: 'var(--color-item-neutral-muted-text)', badge: 'var(--color-item-neutral-badge)' },
}

/**
 * Couleur d'un element en attente, par type : programme bleu, direct orange,
 * pub rose, infoservice marron, jingle et coming next jaune.
 */
export const ITEM_TYPE_COLORS: Record<ItemType, {
  bg: string; border: string; text: string; badge: string
}> = {
  PROGRAM:  { bg: 'var(--color-item-program-bg)', border: 'var(--color-item-program-border)', text: 'var(--color-item-program-text)', badge: 'var(--color-item-program-badge)' },
  LIVE:     { bg: 'var(--color-item-live-bg)', border: 'var(--color-item-live-border)', text: 'var(--color-item-live-text)', badge: 'var(--color-item-live-badge)' },
  AD_BREAK: { bg: 'var(--color-item-ad-bg)', border: 'var(--color-item-ad-border)', text: 'var(--color-item-ad-text)', badge: 'var(--color-item-ad-badge)' },
  INFOSERVICE: { bg: 'var(--color-item-infoservice-bg)', border: 'var(--color-item-infoservice-border)', text: 'var(--color-item-infoservice-text)', badge: 'var(--color-item-infoservice-badge)' },
  JINGLE:   { bg: 'var(--color-item-jingle-bg)', border: 'var(--color-item-jingle-border)', text: 'var(--color-item-jingle-text)', badge: 'var(--color-item-jingle-badge)' },
  INTERLUDE:{ bg: 'var(--color-item-interlude-bg)', border: 'var(--color-item-interlude-border)', text: 'var(--color-item-interlude-text)', badge: 'var(--color-item-interlude-badge)' },
  FILLER:   { bg: 'var(--color-item-neutral-bg)', border: 'var(--color-item-neutral-border)', text: 'var(--color-item-neutral-text)', badge: 'var(--color-item-neutral-badge)' },
}

/** Label français par statut d'item */
export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  SCHEDULED: 'Programmé',
  LOADING:   'Chargement',
  ON_AIR:    'Diffusion',
  DONE:      'Diffusé',
  GAP:       'Interlude',
  ERROR:     'Erreur',
  SKIPPED:   'Ignoré',
}

/** Label français par type d'item */
/**
 * Libellé d'un élément de grille, quelle que soit sa nature.
 * Source unique : bandeau antenne, rail, agenda et playlist doivent nommer un
 * même élément de la même façon.
 */
export function scheduleItemTitle(item: ScheduleItem | null | undefined, fallback: string | null = null): string | null {
  if (!item) return fallback
  if (item.itemType === 'AD_BREAK') return 'Bloc Pubs'
  if (item.itemType === 'INFOSERVICE') return 'Infoservice'
  const overrideTitle = item.itemType === 'LIVE' && typeof item.overrides?.title === 'string'
    ? item.overrides.title.trim()
    : ''
  if (overrideTitle) return overrideTitle
  return item.program?.title ?? item.liveSource?.name ?? item.interludeClip?.name ?? fallback
}

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  PROGRAM:  'Programme',
  LIVE:     'Direct',
  AD_BREAK: 'Pub',
  INFOSERVICE: 'Infoservice',
  JINGLE:   'Élément d’antenne',
  INTERLUDE:'Interlude',
  FILLER:   'Remplissage',
}

/**
 * Libellé d'origine d'un type de programme : le discriminant éditorial
 * générique et les six types d'éléments d'antenne livrés avec le produit. Ce
 * n'est qu'un repli — le libellé affiché vient de `AntennaElementType` dès que
 * la fiche en porte un, et c'est là que la chaîne le renomme.
 */
export const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
  EMISSION:          'Émission',
  JINGLE:            'Jingle',
  COMING_NEXT:       'Coming Next',
  BANDE_ANNONCE:     'Bande Annonce',
  GENERIQUE_ANTENNE: 'Générique Antenne',
  AVERTISSEMENT:     'Avertissement',
  BILLBOARD:         'Billboard',
}

/** Libellés des types de logo */
export const BUG_TYPE_LABELS: Record<BugType, string> = {
  NONE:               'Aucun Logo',
  LMTV_SARTHE:        'Logo LMTV Sarthe',
  LMTV_SARTHE_DIRECT: 'Logo LMTV Sarthe Direct',
  LMTV_SARTHE_INEDIT: 'Logo LMTV Sarthe Inédit',
}

/**
 * Valeurs de `ProgramType` qui désignent un élément d'antenne. Un type ajouté
 * par la chaîne retombe sur `JINGLE` : cette liste reste donc complète, quel
 * que soit le nombre de types personnalisés.
 */
export const ANTENNA_ELEMENT_PROGRAM_TYPES: ProgramType[] = [
  'JINGLE', 'COMING_NEXT', 'BANDE_ANNONCE', 'GENERIQUE_ANTENNE', 'AVERTISSEMENT', 'BILLBOARD',
]

/**
 * Libellé du type d'un élément d'antenne : celui que la chaîne a défini, sinon
 * le libellé d'origine de la valeur d'enum.
 */
export function antennaElementTypeLabel(
  program: { type: ProgramType; antennaElementType?: { label: string } | null },
): string {
  return program.antennaElementType?.label ?? PROGRAM_TYPE_LABELS[program.type]
}

/** Couleur indicateur source live */
export const SOURCE_STATUS_COLORS: Record<SourceStatus, string> = {
  ACTIVE:       'var(--color-signal-active)',
  INACTIVE:     'var(--color-signal-inactive)',
  SIGNAL_LOST:  'var(--color-live)',
  UNSTABLE:     'var(--color-signal-unstable)',
}

/** Couleur badge statut média MAM */
export const MEDIA_STATUS_COLORS: Record<MediaStatus, {
  text: string; bg: string
}> = {
  OK:               { text: 'var(--color-text-success)', bg: 'var(--color-background-success)' },
  MISSING:          { text: 'var(--color-text-danger)', bg: 'var(--color-background-danger)' },
  INVALID_CODEC:    { text: 'var(--color-text-danger)', bg: 'var(--color-background-danger)' },
  DURATION_MISMATCH:{ text: 'var(--color-text-warning)', bg: 'var(--color-background-warning)' },
  UNCHECKED:        { text: 'var(--color-item-neutral-text)', bg: 'var(--color-item-neutral-bg)' },
}

/**
 * Un média est diffusable quand ffprobe l'a validé, et à cette condition seule.
 *
 * `UNCHECKED` signifie « fichier jamais sondé » : c'est l'état des médias
 * hérités d'une installation précédente, dont plus rien ne garantit qu'ils
 * existent. Le scanner de présence du backend promeut en `OK` tout média
 * réellement présent, donc un `UNCHECKED` durable est un vrai problème.
 *
 * Doit rester aligné sur `BROADCASTABLE_MEDIA_STATUSES`
 * (`backend/src/lib/asset-presence.ts`).
 */
export function isBroadcastableMedia(status: MediaStatus): boolean {
  return status === 'OK'
}

/**
 * Indique qu'un bloc de grille qui doit lire un fichier n'en a pas.
 *
 * Les programmes et jingles portent leur MediaAsset directement. Les pubs et
 * infoservices portent un ou plusieurs spots ; les anciens items peuvent
 * encore fournir leur fichier dans `overrides.filePath`.
 */
export function scheduleItemHasMissingMedia(item: ScheduleItem): boolean {
  if (item.itemType === 'PROGRAM' || item.itemType === 'JINGLE') {
    return !item.mediaAsset || item.mediaAsset.status === 'MISSING'
  }

  if (item.itemType === 'AD_BREAK' || item.itemType === 'INFOSERVICE') {
    const overrideFilePath = typeof item.overrides?.filePath === 'string'
      ? item.overrides.filePath.trim()
      : ''
    const placements = item.adPlacements ?? []

    if (placements.length === 0) return overrideFilePath.length === 0
    return placements.some(placement => !placement.spot.filePath?.trim())
  }

  return false
}

// ─── Couverture de grille ─────────────────────────────────────────────────────

/**
 * Retourne la couleur du badge de couverture selon le pourcentage.
 * < 50% → rouge  |  50-80% → amber  |  > 80% → vert
 */
export function coverageColor(pct: number): string {
  if (pct < 50) return 'var(--color-live)'
  if (pct < 80) return 'var(--color-signal-unstable)'
  return 'var(--color-signal-active)'
}

// ─── Taille fichier ───────────────────────────────────────────────────────────

export function formatFileSize(bytes: string | number | null): string {
  if (!bytes) return '—'
  const n = typeof bytes === 'string' ? parseInt(bytes) : bytes
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} GB`
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(0)} MB`
  if (n >= 1_000)         return `${(n / 1_000).toFixed(0)} KB`
  return `${n} B`
}

// ─── Pluriel ──────────────────────────────────────────────────────────────────

export function plural(n: number, singular: string, pluralForm?: string): string {
  return n === 1 ? `${n} ${singular}` : `${n} ${pluralForm ?? singular + 's'}`
}

// ─── Clamp ────────────────────────────────────────────────────────────────────

export function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max)
}
